import { AdvisorBubble, Column } from '@/components'
import { useTranslation } from 'react-i18next'
import { FormAddressAutocomplete } from '../../../../FormItem'
import { LoadingSkeleton, StepLayout } from '../components'
import { useCompany, useUpdateAddress, useUpdateCompany } from '@/services'
import { useChecklistStepper } from '../hooks/useChecklistStepper'
import { useSnackbar } from '@/providers'
import { businessInformationValidationSchema } from '../validationSchema'
import { Company } from '@/types/company'
import { isGenericCompanyDetails } from '@/modules/company/utils/isGenericCompanyDetails'
import {
  BusinessInformationFormValues,
  StepperFormValues,
} from '../utils/types'

const BusinessInformationForm = () => {
  const { t } = useTranslation()

  return (
    <Column gap="16px">
      <Column>
        <FormAddressAutocomplete name="companyDetails" />
      </Column>
      <AdvisorBubble
        text={t(
          'riskChecklist.businessInformationFlow.companyDetails.advisorBubbleText',
        )}
      />
    </Column>
  )
}

const makeInitialValues = (company: Company, formValues: StepperFormValues) => {
  if (formValues.companyDetails?.name && formValues.companyDetails?.address) {
    return formValues
  }

  if (isGenericCompanyDetails(company.name, company.address)) {
    return {
      companyDetails: {
        name: '',
        address: {
          city: '',
          street: '',
          postalCode: '',
          streetNr: '',
        },
      },
    }
  }

  return {
    companyDetails: {
      name: company.name,
      address: company.address,
    },
  }
}

interface BusinessInformationProps {
  companyId: string
  companyAddressId: number
}

export const BusinessInformation = ({
  companyId,
  companyAddressId,
}: BusinessInformationProps) => {
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const updateCompanyMutation = useUpdateCompany(companyId)
  const updateAddressMutation = useUpdateAddress(companyAddressId)
  const { goToNext, formValues } = useChecklistStepper()
  const { data: company, isLoading: isCompanyLoading } = useCompany(companyId)

  const handleSubmit = async (values: BusinessInformationFormValues) => {
    try {
      await updateCompanyMutation.mutateAsync({
        name: values.companyDetails.name,
      })
      await updateAddressMutation.mutateAsync({
        street: values.companyDetails.address.street,
        streetNr: values.companyDetails.address.streetNr,
        postalCode: values.companyDetails.address.postalCode,
        city: values.companyDetails.address.city,
        country: 'Germany',
      })
      goToNext(values)
    } catch {
      snackbar.error(t('snackbars.error.generalMsg'))
    }
  }

  if (isCompanyLoading || !company) {
    return <LoadingSkeleton />
  }

  return (
    <StepLayout
      title={t('riskChecklist.businessInformationFlow.companyDetails.title')}
      subtitle={t(
        'riskChecklist.businessInformationFlow.companyDetails.subtitle',
      )}
      onNext={handleSubmit}
      isLoading={
        updateAddressMutation.isLoading || updateCompanyMutation.isLoading
      }
      initialValues={makeInitialValues(company, formValues)}
      validationSchema={() =>
        businessInformationValidationSchema(
          t('validations', {
            returnObjects: true,
          }),
        )
      }
    >
      <BusinessInformationForm />
    </StepLayout>
  )
}
